import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Card, CardBody, CardHeader, Spinner } from '../components/ui';
import { confirmOrganisationAccess, extractApiErrorMessage } from '../lib/auth-onboarding';

type Phase = 'confirming' | 'done' | 'error';

/**
 * Public double-opt-in landing for a scope-b "request access" flow. The
 * requester arrives here from the confirm link in their email with the
 * capability token in the URL fragment (#token=<jwt>). We lift the token out of
 * the fragment, scrub the address bar so the bearer token never lingers in
 * history or a referrer header, and POST it to the confirm endpoint on mount.
 * The endpoint is idempotent, so a replayed link still lands on the success
 * state without re-notifying the org's admins.
 */
export default function RequestAccessConfirmPage(): ReactElement {
  const [phase, setPhase] = useState<Phase>('confirming');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const didInit = useRef(false);

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;

    // Lift the token out of the fragment, then scrub it from the URL.
    const token = new URLSearchParams(window.location.hash.replace(/^#/, '')).get('token');
    if (window.location.hash) {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }

    if (!token) {
      setErrorMsg('This confirmation link is missing or has expired. Please open the link from your email again.');
      setPhase('error');
      return;
    }

    // The `didInit` guard above already ensures this effect body runs once,
    // even under React.StrictMode's dev double-invoke and even though we read
    // (and scrub) the one-shot URL fragment here. A `cancelled` cleanup would
    // contradict that guard: StrictMode's cleanup would flip `cancelled` before
    // the re-run bailed on `didInit`, leaving the resolved POST unable to call
    // setPhase and the page stuck on the spinner. The confirm endpoint is
    // idempotent, so firing exactly once and always committing the result is
    // correct.
    confirmOrganisationAccess(token)
      .then(() => {
        setPhase('done');
      })
      .catch((err) => {
        setErrorMsg(
          extractApiErrorMessage(err, 'This confirmation link is invalid or has expired. Please request access again.'),
        );
        setPhase('error');
      });
  }, []);

  return (
    <div className="mx-auto max-w-xl px-4 py-12 sm:px-6 lg:px-8">
      <Card>
        <CardHeader title="Confirm your request" />
        <CardBody>
          {phase === 'confirming' ? (
            <div className="flex items-center justify-center py-8">
              <Spinner />
            </div>
          ) : phase === 'done' ? (
            <div className="flex flex-col gap-4">
              <Alert
                variant="success"
                message="Request sent to your organisation's admins. They'll review it and send you an invite if approved."
              />
              <Link to="/" className="font-semibold text-brand-carnaby-pink hover:underline">
                Back to Reed.ai
              </Link>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <Alert variant="error" message={errorMsg ?? 'Something went wrong. Please try again.'} />
              <Link to="/onboarding/employer" className="font-semibold text-brand-carnaby-pink hover:underline">
                Start again
              </Link>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
